import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu as MenuIcon, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { RESTAURANT, IMAGES } from "../lib/restaurant";
import { useLang } from "../i18n/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const NAV = [
    { to: "/",        label: t.nav.home },
    { to: "/o-nas",   label: t.nav.about },
    { to: "/menu",    label: t.nav.menu },
    { to: "/galeria", label: t.nav.gallery },
    { to: "/kontakt", label: t.nav.contact },
  ];

  const isHome = location.pathname === "/";
  const transparentAtTop = isHome && !scrolled;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparentAtTop
          ? "bg-gradient-to-b from-black/70 via-black/40 to-transparent"
          : "bg-charcoal/95 backdrop-blur-md border-b border-white/10"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img
            src={IMAGES.logo}
            alt="Segreto Ristorante Italiano"
            className="h-14 w-14 rounded-full object-cover ring-1 ring-gold/40 group-hover:ring-gold transition shadow-lg shadow-black/40"
          />
          <div className="hidden sm:block leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]">
            <div className="text-cream font-serif text-xl tracking-wide">Segreto</div>
            <div className="text-cream/80 uppercase-track text-[10px]">Ristorante Italiano</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm uppercase-track font-medium transition-colors drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)] ${
                  isActive ? "text-gold" : "text-cream hover:text-gold"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher variant="header" />
          <a
            href={`tel:${RESTAURANT.phoneRaw}`}
            data-testid="header-phone-mobile"
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full bg-wine text-cream hover:bg-wine/85 transition"
            aria-label="Call Segreto"
          >
            <Phone className="h-4 w-4" />
          </a>
          <a href="tel:+48518805767" className="hidden sm:inline-flex">
            <Button
              data-testid="header-reserve-button"
              className="rounded-none bg-wine text-cream hover:bg-wine/85 uppercase-track text-xs h-11 px-6 border border-wine"
            >
              {t.nav.reserve}
            </Button>
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="lg:hidden inline-flex items-center justify-center h-11 w-11 text-cream border border-white/20 hover:border-gold transition"
                aria-label="Open menu"
              >
                <MenuIcon className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-charcoal border-l border-white/10 text-cream w-[85vw] sm:w-[420px]">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex flex-col h-full pt-2">
                <div className="flex items-center gap-3 mb-8">
                  <img src={IMAGES.logo} alt="" className="h-10 w-10 rounded-full" />
                  <div className="font-serif text-2xl">Segreto</div>
                </div>
                <nav className="flex flex-col gap-4">
                  {NAV.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.to === "/"}
                      data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                      className={({ isActive }) =>
                        `font-serif text-3xl ${isActive ? "text-gold" : "text-cream"} hover:text-gold transition`
                      }
                    >
                      {n.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="mt-6">
                  <LanguageSwitcher variant="mobile" />
                </div>
                <div className="mt-auto space-y-3 pb-4">
                  <a
                    href={`tel:${RESTAURANT.phoneRaw}`}
                    data-testid="mobile-phone-cta"
                    className="flex items-center justify-center gap-2 h-12 bg-wine text-cream uppercase-track text-xs"
                  >
                    <Phone className="h-4 w-4" /> {RESTAURANT.phone}
                  </a>
                  <a
                    href="tel:+48518805767"
                    data-testid="mobile-reserve-cta"
                    className="flex items-center justify-center h-12 border border-cream text-cream uppercase-track text-xs hover:bg-cream hover:text-charcoal transition"
                  >
                    {t.nav.reserve}
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
